import { logger } from '../lib/utils';
import { useState, useCallback, useEffect, useRef } from 'react';

const API_URL = process.env.REACT_APP_BACKEND_URL;
const USED_IMAGES_KEY = 'jigsaw_used_images';
const VIEWING_HISTORY_KEY = 'jigsaw_viewing_history';

// Helper to get used images from localStorage
const getUsedImages = () => {
  try {
    const stored = localStorage.getItem(USED_IMAGES_KEY);
    return stored ? JSON.parse(stored) : [];
  } catch {
    return [];
  }
};

// Helper to save used images to localStorage
const saveUsedImages = (images) => {
  try {
    localStorage.setItem(USED_IMAGES_KEY, JSON.stringify(images));
  } catch (e) {
    logger.error('Failed to save used images:', e);
  }
};

// Helper to get viewing history from localStorage
const getViewingHistory = () => {
  try {
    const stored = localStorage.getItem(VIEWING_HISTORY_KEY);
    return stored ? JSON.parse(stored) : [];
  } catch {
    return [];
  }
};

// Helper to save viewing history to localStorage
const saveViewingHistory = (history) => {
  try {
    localStorage.setItem(VIEWING_HISTORY_KEY, JSON.stringify(history));
  } catch (e) {
    logger.error('Failed to save viewing history:', e);
  }
};

/**
 * Custom hook for managing puzzle images.
 * Handles fetching, tracking used images, and navigation.
 */
export function useImageManager(deviceId = null) {
  const [imageUrl, setImageUrl] = useState(null);
  const [imageList, setImageList] = useState([]);
  const [currentImageIndex, setCurrentImageIndex] = useState(0);
  const [isLoadingImage, setIsLoadingImage] = useState(true);
  const [usedImages, setUsedImages] = useState(() => getUsedImages());
  
  // Viewing history tracks the ORDER in which images were viewed (for Previous button)
  const [viewingHistory, setViewingHistory] = useState(() => getViewingHistory());
  const [historyIndex, setHistoryIndex] = useState(-1); // -1 means at the end (most recent)

  // Clean up stale localStorage data when imageList changes
  // This removes URLs that are no longer in the current weekly rotation
  useEffect(() => {
    if (imageList.length === 0) return;
    
    // Clean up usedImages - keep only images in current list
    setUsedImages(prev => {
      const cleaned = prev.filter(img => imageList.includes(img));
      if (cleaned.length !== prev.length) {
        logger.log(`Cleaned usedImages: removed ${prev.length - cleaned.length} stale URLs`);
        saveUsedImages(cleaned);
      }
      return cleaned;
    });
    
    // Clean up viewingHistory - keep only images in current list
    setViewingHistory(prev => {
      const cleaned = prev.filter(img => imageList.includes(img));
      if (cleaned.length !== prev.length) {
        logger.log(`Cleaned viewingHistory: removed ${prev.length - cleaned.length} stale URLs`);
        saveViewingHistory(cleaned);
      }
      return cleaned;
    });
  }, [imageList]);

  // Mark an image as used and persist to localStorage
  const markImageAsUsed = useCallback((imgUrl) => {
    if (!imgUrl) return;
    setUsedImages(prev => {
      if (prev.includes(imgUrl)) return prev;
      const updated = [...prev, imgUrl];
      saveUsedImages(updated);
      return updated;
    });
  }, []);

  // Add image to viewing history (called when a new image is selected)
  const addToViewingHistory = useCallback((imgUrl) => {
    if (!imgUrl) return;
    setViewingHistory(prev => {
      // If we're in the middle of history (after pressing Previous), 
      // we need to truncate and add new path
      const currentIndex = historyIndex === -1 ? prev.length : historyIndex + 1;
      const truncated = prev.slice(0, currentIndex);
      
      // Don't add if same as most recent
      if (truncated.length > 0 && truncated[truncated.length - 1] === imgUrl) {
        return prev;
      }
      
      const updated = [...truncated, imgUrl];
      // Keep only last 100 entries to prevent localStorage bloat
      const limited = updated.slice(-100);
      saveViewingHistory(limited);
      return limited;
    });
    // Reset history index to end (most recent)
    setHistoryIndex(-1);
  }, [historyIndex]);

  // Get available (unused) images from the list
  // Does NOT reset when empty - we want to show the "no new images" message
  const getAvailableImages = useCallback((images) => {
    return images.filter(img => !usedImages.includes(img));
  }, [usedImages]);
  
  // Check if there are new images available
  const hasNewImages = imageList.length > 0 && imageList.some(img => !usedImages.includes(img));

  // Fetch image list from API
  const fetchImageList = useCallback(async () => {
    try {
      const response = await fetch(`${API_URL}/api/images/list`);
      const data = await response.json();
      return data.images || [];
    } catch (error) {
      logger.error('Failed to fetch image list:', error);
      return [];
    }
  }, []);

  // Fetch a random image from server (with device exclusion)
  const fetchRandomImageFromServer = useCallback(async () => {
    if (!deviceId) return null;
    
    try {
      const params = `?device_id=${encodeURIComponent(deviceId)}`;
      const response = await fetch(`${API_URL}/api/images/random${params}`);
      const data = await response.json();
      return data.image_url || null;
    } catch (error) {
      logger.error('Failed to fetch random image from server:', error);
      return null;
    }
  }, [deviceId]);

  // Get a random unused image (server-first, fallback to local)
  // Random ONLY selects NEW/unused images from the weekly cache
  const getRandomImage = useCallback(async () => {
    setIsLoadingImage(true);
    
    // Try server-side selection first (server already excludes used images)
    const serverImage = await fetchRandomImageFromServer();
    if (serverImage && serverImage !== imageUrl) {
      const index = imageList.indexOf(serverImage);
      if (index !== -1) {
        setCurrentImageIndex(index);
      }
      markImageAsUsed(serverImage);
      addToViewingHistory(serverImage);
      setImageUrl(serverImage);
      setIsLoadingImage(false);
      return serverImage;
    }
    
    // Fallback to local selection - only pick from UNUSED images
    if (imageList.length === 0) {
      logger.log('getRandomImage: No images in list');
      setIsLoadingImage(false);
      return null;
    }
    
    let availableImages = getAvailableImages(imageList);
    // Also exclude current image to ensure we get a different one
    availableImages = availableImages.filter(img => img !== imageUrl);
    
    if (availableImages.length === 0) {
      // All images have been used - show a message but don't reset
      logger.log('getRandomImage: No new images available');
      setIsLoadingImage(false);
      return null;
    }
    
    const randomIndex = Math.floor(Math.random() * availableImages.length);
    const selectedImage = availableImages[randomIndex];
    
    const index = imageList.indexOf(selectedImage);
    if (index !== -1) {
      setCurrentImageIndex(index);
    }
    
    markImageAsUsed(selectedImage);
    addToViewingHistory(selectedImage);
    setImageUrl(selectedImage);
    setIsLoadingImage(false);
    return selectedImage;
  }, [imageList, imageUrl, getAvailableImages, markImageAsUsed, addToViewingHistory, fetchRandomImageFromServer]);

  // Navigate to previous image in viewing history
  // Previous scrolls BACKWARDS through images you've ALREADY viewed (in viewing order)
  const previousImage = useCallback(() => {
    // Filter viewing history to only include images still in the current week's imageList
    const validHistory = viewingHistory.filter(img => imageList.includes(img));
    
    if (validHistory.length === 0) {
      logger.log('previousImage: No viewing history available');
      return null;
    }
    
    // Calculate current position in history
    const currentHistoryIndex = historyIndex === -1 ? validHistory.length - 1 : historyIndex;
    
    // Can't go further back than the start
    if (currentHistoryIndex <= 0) {
      logger.log('previousImage: Already at the beginning of history');
      return null;
    }
    
    // Go to previous image in history
    const newIndex = currentHistoryIndex - 1;
    const selectedImage = validHistory[newIndex];
    
    // Skip if same as current
    if (selectedImage === imageUrl) {
      // Try one more back
      if (newIndex > 0) {
        const furtherBackImage = validHistory[newIndex - 1];
        setHistoryIndex(newIndex - 1);
        const index = imageList.indexOf(furtherBackImage);
        if (index !== -1) {
          setCurrentImageIndex(index);
        }
        setImageUrl(furtherBackImage);
        return furtherBackImage;
      }
      return null;
    }
    
    setHistoryIndex(newIndex);
    const index = imageList.indexOf(selectedImage);
    if (index !== -1) {
      setCurrentImageIndex(index);
    }
    
    setImageUrl(selectedImage);
    return selectedImage;
  }, [viewingHistory, historyIndex, imageList, imageUrl]);

  // Navigate to next image in viewing history (for going forward after pressing Previous)
  const nextImageInHistory = useCallback(() => {
    // Filter viewing history to only include images still in the current week's imageList
    const validHistory = viewingHistory.filter(img => imageList.includes(img));
    
    if (validHistory.length === 0 || historyIndex === -1) {
      // Already at the end or no history
      return null;
    }
    
    const newIndex = historyIndex + 1;
    
    if (newIndex >= validHistory.length) {
      // Reached the end of history
      setHistoryIndex(-1);
      return null;
    }
    
    const selectedImage = validHistory[newIndex];
    setHistoryIndex(newIndex);
    
    const index = imageList.indexOf(selectedImage);
    if (index !== -1) {
      setCurrentImageIndex(index);
    }
    
    setImageUrl(selectedImage);
    return selectedImage;
  }, [viewingHistory, historyIndex, imageList]);

  // Set a specific image (e.g., from challenge or history)
  const selectImage = useCallback((url) => {
    const index = imageList.indexOf(url);
    if (index !== -1) {
      setCurrentImageIndex(index);
    }
    markImageAsUsed(url);
    addToViewingHistory(url);
    setImageUrl(url);
    return url;
  }, [imageList, markImageAsUsed, addToViewingHistory]);

  // Track if initialization has been done
  const initializedRef = useRef(false);

  // Helper to add image to viewing history during initialization
  const addToHistoryDirect = (imgUrl, images) => {
    if (!imgUrl) return;
    const currentHistory = getViewingHistory();
    // Filter to only images in current week and add new one
    const validHistory = currentHistory.filter(img => images.includes(img));
    if (validHistory.length === 0 || validHistory[validHistory.length - 1] !== imgUrl) {
      const updated = [...validHistory, imgUrl].slice(-100);
      saveViewingHistory(updated);
      setViewingHistory(updated);
    } else {
      setViewingHistory(validHistory);
    }
  };

  // Initialize on mount (only once)
  useEffect(() => {
    if (initializedRef.current) return;
    
    const initialize = async () => {
      initializedRef.current = true;
      setIsLoadingImage(true);
      const images = await fetchImageList();
      setImageList(images);
      
      if (images.length > 0) {
        // Try server-side selection
        if (deviceId) {
          try {
            const params = `?device_id=${encodeURIComponent(deviceId)}`;
            const response = await fetch(`${API_URL}/api/images/random${params}`);
            const data = await response.json();
            
            if (data.image_url) {
              const index = images.indexOf(data.image_url);
              if (index !== -1) {
                setCurrentImageIndex(index);
              }
              setImageUrl(data.image_url);
              markImageAsUsed(data.image_url);
              addToHistoryDirect(data.image_url, images);
              setIsLoadingImage(false);
              return;
            }
          } catch (error) {
            logger.error('Failed to fetch initial image from server:', error);
          }
        }
        
        // Fallback to local selection
        const currentUsed = getUsedImages();
        let availableImages = images.filter(img => !currentUsed.includes(img));
        
        if (availableImages.length === 0) {
          availableImages = images;
          setUsedImages([]);
          saveUsedImages([]);
        }
        
        const randomIndex = Math.floor(Math.random() * availableImages.length);
        const initialImage = availableImages[randomIndex];
        
        const index = images.indexOf(initialImage);
        if (index !== -1) {
          setCurrentImageIndex(index);
        }
        
        setImageUrl(initialImage);
        const newUsed = [...currentUsed.filter(img => images.includes(img)), initialImage];
        setUsedImages(newUsed);
        saveUsedImages(newUsed);
        addToHistoryDirect(initialImage, images);
      }
      
      setIsLoadingImage(false);
    };
    
    initialize();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [deviceId]);

  // Check if this is the first play for a specific image
  const checkFirstPlay = useCallback(async (imageUrlToCheck) => {
    if (!deviceId || !imageUrlToCheck) return true; // Default to first play if no device ID
    
    try {
      const response = await fetch(`${API_URL}/api/images/check-first-play`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          device_id: deviceId,
          image_url: imageUrlToCheck
        })
      });
      
      if (response.ok) {
        const data = await response.json();
        return data.is_first_play;
      }
      return true; // Default to first play on error
    } catch (error) {
      logger.error('Error checking first play:', error);
      return true; // Default to first play on error
    }
  }, [deviceId]);

  // Calculate if we can go back in history
  // Only calculate when imageList is populated (not during initial load)
  const validHistory = imageList.length > 0 
    ? viewingHistory.filter(img => imageList.includes(img))
    : [];
  const currentHistoryPosition = historyIndex === -1 ? validHistory.length - 1 : historyIndex;
  const canGoBack = validHistory.length > 1 && currentHistoryPosition > 0;
  const canGoForward = historyIndex !== -1 && historyIndex < validHistory.length - 1;

  return {
    imageUrl,
    setImageUrl,
    imageList,
    currentImageIndex,
    isLoadingImage,
    usedImagesCount: usedImages.length,
    totalImagesCount: imageList.length,
    hasNewImages,
    markImageAsUsed,
    getRandomImage,
    previousImage,
    nextImageInHistory,
    selectImage,
    viewingHistoryLength: validHistory.length,
    canGoBack,
    canGoForward,
    checkFirstPlay
  };
}
